/**
 * Grade de "Critérios de Aceitação" do PME — mapeia as mensagens de
 * subscrição do InsureMO para a estrutura do protótipo: nome amigável por
 * critério, uma linha por sub-regra (texto SEM o resultado embutido), um
 * resultado por linha e um status POR LINHA — cada sub-regra responde por si,
 * a que reprovou fica laranja/"Análise" e a que passou cinza/"OK".
 *
 * A fonte é a resposta do endpoint de underwriting messages
 * (`BusinessUnderwritingMessagesResponse`): `messages` traz código, texto
 * renderizado e nível U0/U1; `underwritingRuns` traz o `MessageParams` cru de
 * cada mensagem (JSON array, casado por `UwRuleMessageId`).
 *
 * CONVENÇÃO DE AUTORIA DAS REGRAS (plataforma): os params são posicionais no
 * padrão `[limite, resultado, limite2, resultado2, ...]` e cada sub-critério
 * tem exatamente um `(resultado: X)` no template da mensagem. Regra nova na
 * grade = uma entrada nova no CATALOGO abaixo — nada mais.
 *
 * Mensagem fora do catálogo cai no fallback (nome técnico + texto integral),
 * então uma regra recém-criada nunca quebra a tela.
 */
import type {
  BusinessUnderwritingMessage,
  BusinessUnderwritingMessagesResponse,
} from '@/services/quotes/types';

const DASH = '—';

export type AcceptanceCriterionView = {
  name: string;
  rules: string[];
  results: string[];
  /** Veredito POR SUB-REGRA (índice casa com `rules`/`results`): true = passou
   *  (fica cinza mesmo com o critério em análise); null = sem avaliação própria
   *  (segue o status do critério). */
  ruleOk: Array<boolean | null>;
  /**
   * Status POR SUB-REGRA (índice casa com `rules`/`results`). Cada linha tem o
   * seu: a que reprovou fica `Análise` (laranja), a que passou fica `OK`
   * (cinza). Linha sem veredito próprio herda o status do critério.
   */
  lineStatus: Array<'OK' | 'Análise'>;
  /** Status do critério inteiro — `Análise` se qualquer sub-regra reprovou. */
  status: 'OK' | 'Análise';
};

export type AcceptanceCriterionMember = {
  capital?: string;
  awayFromWork?: boolean;
};

type CatalogLine = {
  /** Texto fixo da sub-regra; `{n}` interpola `params[n]` (ex.: limites dinâmicos). */
  text: string;
  /** Índice do param que é o RESULTADO desta linha; null = sem resultado ("—"). */
  resultIndex: number | null;
  /**
   * Resultado já resolvido, para linhas montadas por `resolveLines`: nelas o
   * valor não é um param posicional e sim um pedaço de uma LISTA que veio
   * dentro de um param só. Tem precedência sobre `resultIndex`.
   */
  rawResult?: string;
  /** Formatação do resultado (percent: "11.11" → "11,11%"). */
  kind?: 'percent';
  /** A sub-regra passou? Recebe o resultado CRU e os params; sub-regra que
   *  passou fica cinza mesmo quando o critério foi para análise. */
  isOk?: (rawResult: string, params: string[]) => boolean;
  /**
   * Some a sub-regra da grade quando ela não se aplica àquela cotação — caso
   * do critério por COBERTURA, em que a mensagem traz sempre as nove linhas da
   * tabela do produto e só as coberturas efetivamente contratadas interessam.
   * Sem isso, a grade encheria de linhas "OK" de cobertura não vendida.
   */
  hideWhen?: (rawResult: string, params: string[]) => boolean;
};

type CatalogEntry = {
  label: string;
  order: number;
  lines: CatalogLine[];
  /**
   * Regras cuja QUANTIDADE de linhas varia por cotação — a mensagem traz uma
   * LISTA dentro de um único param, em vez de uma grade fixa de sub-regras.
   * Quando presente, substitui `lines` e monta as sub-regras a partir dos
   * params (ou do texto renderizado, quando não há params).
   */
  resolveLines?: (params: string[], context: CriterionContext) => CatalogLine[];
  defaultParams?: string[];
  defaultWhenZeroParamIndexes?: number[];
  showOnlyWhenUnderAnalysis?: boolean;
};

/** Contexto da cotação disponível para as linhas dinâmicas. */
type CriterionContext = {
  /** Texto renderizado da mensagem — fonte de reserva quando faltam params. */
  detail: string;
  /** Catálogo de coberturas do produto (código → nome), vindo da tela. */
  coverageNameByCode?: ReadonlyMap<string, string>;
};

export type AcceptanceCriteriaOptions = {
  /**
   * Código → nome da cobertura (schema do produto). Sem ele, as regras que
   * citam coberturas exibem o código cru — nenhuma quebra.
   */
  coverageNameByCode?: ReadonlyMap<string, string>;
};

/** "7.41" | "7,41%" → 7.41 (percentuais e contagens usam ponto decimal). */
function parsePercentValue(raw: string): number {
  const value = Number.parseFloat(
    raw.trim().replace(/%$/, '').replace(',', '.')
  );
  return Number.isFinite(value) ? value : Number.NaN;
}

/** "R$ 500.000,00" → 500000 (formato BRL: milhar com ponto, decimal com vírgula). */
function parseBrlValue(raw: string): number {
  const value = Number.parseFloat(
    raw
      .replace(/[^\d,.-]/g, '')
      .replace(/\./g, '')
      .replace(',', '.')
  );
  return Number.isFinite(value) ? value : Number.NaN;
}

function calculateAwayFromWorkCapitalPercentage(
  members: AcceptanceCriterionMember[]
): string {
  const capitals = members.map(member => ({
    capital: parseBrlValue(member.capital ?? ''),
    awayFromWork: member.awayFromWork === true,
  }));
  const totalCapital = capitals.reduce(
    (total, member) =>
      total + (Number.isFinite(member.capital) ? member.capital : 0),
    0
  );
  const awayFromWorkCapital = capitals.reduce(
    (total, member) =>
      total +
      (member.awayFromWork && Number.isFinite(member.capital)
        ? member.capital
        : 0),
    0
  );
  const percentage =
    totalCapital > 0 ? (awayFromWorkCapital / totalCapital) * 100 : 0;
  return percentage.toFixed(2);
}

function normalizeText(raw: string): string {
  return raw
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .trim()
    .toUpperCase();
}

/**
 * Coberturas da tabela de LIMITES ABSOLUTOS, na MESMA ordem em que a regra
 * `930016_UW_13_LimiteAbsolutoCobertura` monta os params — mexer aqui sem
 * mexer lá (e vice-versa) desalinha resultado e cobertura.
 */
const LIMITE_ABSOLUTO_COVERAGE_NAMES = [
  'Morte',
  'Morte Acidental',
  'Invalidez Permanente Total ou Parcial por Acidente',
  'Invalidez Funcional Permanente Total por Doença',
  'Morte Cônjuge',
  'Morte Acidental Cônjuge',
  'Invalidez Funcional Permanente Total por Doença Cônjuge',
  'Doenças Graves Cônjuge',
  'Invalidez Permanente Total ou Parcial por Acidente Cônjuge',
] as const;

/**
 * Limite de cobertura (`930016_UW_16_CoverageLimitSubscription`) — diferente das
 * demais regras, ela devolve uma LISTA de coberturas reprovadas num param só:
 * `9300001 (capital R$ 800.000,00 / limite R$ 500.000,00); 8200002 (...)`.
 * A quantidade de itens varia por cotação, então as linhas da grade são
 * montadas a partir do param, uma por cobertura.
 */
const COVERAGE_LIMIT_ITEM =
  /^(.+?)\s*\(\s*capital\s*(.+?)\s*\/\s*limite\s*(.+?)\s*\)$/i;

type CoverageLimitItem = { code: string; capital: string; limit: string };

/** Param cru quando existe; senão, a lista é recuperada do texto renderizado
 *  (tudo depois do `:`, sem o ponto final). */
function extractCoverageLimitSource(params: string[], detail: string): string {
  const raw = params[0]?.trim();
  if (raw) return raw;
  const colonIndex = detail.indexOf(':');
  if (colonIndex < 0) return '';
  return detail
    .slice(colonIndex + 1)
    .trim()
    .replace(/\.\s*$/, '');
}

/**
 * Coberturas que exigem análise de subscrição
 * (`930016_UW_28_CoverageRequiresSubscription`). A regra devolve a lista num
 * param só, no formato `8400001 - Doenças Graves; 8200015 - Diárias ...`.
 * Cada item vira uma linha da grade, só com o nome.
 */
const RESTRICTED_COVERAGE_ITEM = /^(\d+)\s*-\s*(.+)$/;

function extractRestrictedCoveragesSource(
  params: string[],
  detail: string
): string {
  const raw = params[0]?.trim();
  if (raw) return raw;
  const colonIndex = detail.indexOf(':');
  if (colonIndex < 0) return '';
  return detail
    .slice(colonIndex + 1)
    .trim()
    .replace(/\.\s*$/, '');
}

function parseRestrictedCoverageNames(
  source: string,
  coverageNameByCode?: ReadonlyMap<string, string>
): string[] {
  return source
    .split(';')
    .map(item => item.trim().replace(/\.\s*$/, ''))
    .filter(Boolean)
    .map(item => {
      const match = RESTRICTED_COVERAGE_ITEM.exec(item);
      if (!match) return coverageNameByCode?.get(item) ?? item;
      const code = match[1].trim();
      const embeddedName = match[2].trim();
      return coverageNameByCode?.get(code) ?? embeddedName;
    });
}

function parseCoverageLimitItems(source: string): CoverageLimitItem[] {
  return source
    .split(';')
    .map(item => item.trim())
    .filter(Boolean)
    .map(item => {
      const match = COVERAGE_LIMIT_ITEM.exec(item);
      // Item fora do formato esperado vira linha com o texto cru e sem
      // resultado — a grade não quebra por causa de uma mensagem nova.
      if (!match) return { code: item, capital: '', limit: '' };
      return {
        code: match[1].trim(),
        capital: match[2].trim(),
        limit: match[3].trim(),
      };
    });
}

/** Idade Máxima no Seguro Novo — resultado = quantidade de vidas acima de 70
 *  anos; passou quando zero. */
const IDADE_MAXIMA_NEW_INSURANCE_LINE: CatalogLine = {
  text: 'Quando SEGURO NOVO, idade deve ser menor ou igual a 70 anos',
  resultIndex: 0,
  isOk: raw => parsePercentValue(raw) === 0,
};

/** Tipo de seguro da mensagem → como aparece no texto da regra
 *  ("Transferência de Congênere" → "TRANSFERÊNCIA"). */
function insuranceTypeTitle(insuranceType: string): string {
  const normalized = normalizeText(insuranceType);
  if (normalized.startsWith('RENOVACAO')) return 'RENOVAÇÃO';
  if (normalized.startsWith('TRANSFERENCIA')) return 'TRANSFERÊNCIA';
  return insuranceType.trim().toUpperCase();
}

/** Idade Máxima na Renovação/Transferência — uma linha só: reprova quando há
 *  vida com 70 anos ou mais E a quantidade OU o capital dessas vidas passa de
 *  3%. O resultado traz os dois percentuais, um abaixo do outro. */
function idadeMaximaRenewalLine(params: string[]): CatalogLine {
  const quantity = params[1] ?? '';
  const capital = params[3] ?? '';
  return {
    text: `Quando ${insuranceTypeTitle(params[2] ?? '')}, a quantidade ou o capital das vidas com 70 anos ou mais deve ser menor ou igual a 3%`,
    resultIndex: null,
    rawResult: `Vidas ${formatPercent(quantity || '0.00')}\nCapital ${formatPercent(capital || '0.00')}`,
    isOk: () =>
      parsePercentValue(params[0] ?? '') === 0 ||
      (!(parsePercentValue(quantity) > 3) && !(parsePercentValue(capital) > 3)),
  };
}

/** Mensagem antiga, sem o tipo de seguro nos params — grade de antes. */
const IDADE_MAXIMA_LEGACY_LINES: CatalogLine[] = [
  IDADE_MAXIMA_NEW_INSURANCE_LINE,
  {
    text: 'O montante de vidas acima de 70 anos deve ser menor ou igual a 3%',
    resultIndex: 1,
    kind: 'percent',
    isOk: raw => parsePercentValue(raw) <= 3,
  },
];

/** Teto de concentração usado quando nem params nem texto trazem o limite. */
const CAPITAL_CONCENTRATION_DEFAULT_LIMIT = '30';

/** Capital Segurado — percentual de vidas com capital acima de R$ 200 mil.
 *  O resultado vem por `resultIndex` (params) ou já resolvido (`rawResult`). */
function capitalConcentrationLine(
  rawLimit: string | undefined,
  result: Pick<CatalogLine, 'resultIndex' | 'rawResult'>
): CatalogLine {
  const limit = rawLimit?.trim() || CAPITAL_CONCENTRATION_DEFAULT_LIMIT;
  return {
    text: `Percentual de vidas com capital acima de R$ 200.000,00 deve ser menor ou igual a ${limit}%`,
    ...result,
    kind: 'percent',
    isOk: raw => parsePercentValue(raw) <= parsePercentValue(limit),
  };
}

/** Limite de concentração lido do texto renderizado ("... igual a 30% ..."). */
function extractConcentrationLimitFromDetail(detail: string): string {
  return (
    /igual a\s*([\d.,]+)\s*%/i.exec(detail)?.[1] ??
    CAPITAL_CONCENTRATION_DEFAULT_LIMIT
  );
}

/**
 * Regras que NÃO aparecem na grade de Critérios de Aceitação, por
 * `messageCode`.
 *
 * Tirar a entrada do `CATALOGO` não basta: sem ela a mensagem cai no fallback e
 * aparece do mesmo jeito, só que com o nome técnico e o texto cru do InsureMO.
 * Para sumir da tela o código precisa estar aqui.
 *
 * ATENÇÃO: isto é só exibição. A regra continua rodando no InsureMO e, se vier
 * U1, continua mandando a cotação/proposta para a análise técnica — o
 * subscritor verá o encaminhamento sem este motivo listado.
 *
 * Para trazer a regra de volta: remova o código daqui e (re)adicione a entrada
 * no `CATALOGO`, senão ela volta pelo fallback.
 */
const REGRAS_OCULTAS = new Set([
  // "Atividade de Risco" — ocultada a pedido do time.
  'ALBA-PA-UW-B0004',
]);

/** Uma entrada por `messageCode` (ou `ruleName`, ver `findCatalogEntry`) das
 *  regras de subscrição do 930016. */
/**
 * As seis sub-regras "quantidade + valor" da ANÁLISE DE CRÉDITO (Serasa) têm a
 * mesma forma: dois params de limite, dois de resultado, e veredito
 * INDEPENDENTE por linha — no InsureMO a regra reprova se quantidade OU valor
 * estourar, então a linha que passou fica cinza/OK e a que estourou fica
 * laranja/Análise. Uma fábrica evita seis blocos idênticos.
 *
 * Limites do documento de negócio "Apoio Ajuste SERASA": 20 ocorrências e
 * R$ 80.000. Os defaults aqui são só rede de segurança — quem manda são os
 * params da mensagem, para a grade não ter número cravado no front.
 */
function serasaCountAndAmountLines(criterion: string): CatalogLine[] {
  return [
    {
      text: `Quantidade de ocorrências de ${criterion} deve ser menor ou igual a {0}`,
      resultIndex: 1,
      isOk: (raw, params) =>
        parsePercentValue(raw) <= parsePercentValue(params[0] ?? '20'),
    },
    {
      text: `Valor total de ${criterion} deve ser menor ou igual a {2}`,
      resultIndex: 3,
      isOk: (raw, params) =>
        parseBrlValue(raw) <= parseBrlValue(params[2] ?? 'R$ 80.000,00'),
    },
  ];
}

const CATALOGO: Record<string, CatalogEntry> = {
  /*
   * Capital Segurado (`930016_UW_03_CapitalSegurado`) — uma linha só, a de
   * concentração. Formato ATUAL: 2 params `[limite %, % concentração]`.
   *
   * Mensagens antigas (4 ou 6 params: limite capital, maior capital, limite %,
   * % concentração, quantidade de vidas, gatilho de vidas) também traziam o
   * teto de capital individual; essa sub-regra NÃO aparece mais na grade —
   * delas só se aproveita a concentração.
   */
  'ALBA-PA-UW-B9901': {
    label: 'Capital Segurado',
    order: 20,
    lines: [],
    resolveLines: (params, { detail }) => {
      if (params.length >= 4) {
        return [capitalConcentrationLine(params[2], { resultIndex: 3 })];
      }
      if (params.length > 0) {
        return [capitalConcentrationLine(params[0], { resultIndex: 1 })];
      }
      // Sem params crus: limite e resultado vêm do texto renderizado. A
      // concentração é sempre o ÚLTIMO "(resultado: X)" — no legado o do
      // capital vinha antes.
      const results = extractResultsFromDetail(detail);
      return [
        capitalConcentrationLine(extractConcentrationLimitFromDetail(detail), {
          resultIndex: null,
          rawResult: results[results.length - 1],
        }),
      ];
    },
  },
  'ALBA-PA-UW-B9911': {
    label: 'DPS',
    order: 30,
    showOnlyWhenUnderAnalysis: true,
    defaultParams: ['R$ 100.000,00', 'R$ 100.000,00'],
    defaultWhenZeroParamIndexes: [1],
    lines: [
      {
        text: 'Capital de dispensa de DPS não pode ser maior que {0}',
        resultIndex: 1,
        isOk: (raw, params) =>
          parseBrlValue(raw) <= parseBrlValue(params[0] ?? ''),
      },
    ],
  },
  /*
   * Comparativo de Corretores — a regra `930016_UW_15` já decide pelo campo
   * que o portal materializa (`QuoteAnotherBroker`) e manda só o veredito.
   * Um param, uma linha.
   */
  'ALBA-PA-UW-B9904': {
    label: 'Comparativo de Corretores',
    order: 35,
    lines: [
      {
        text: 'Cotações em andamento, que possuam corretores diferentes, deve ser igual a NÃO',
        resultIndex: 0,
        isOk: raw => normalizeText(raw) === 'NAO',
      },
    ],
  },
  'ALBA-PA-UW-B9905': {
    label: 'Histórico de afastamento',
    order: 40,
    lines: [
      {
        text: 'CID com restrição deve ser igual a NÃO',
        resultIndex: 0,
        isOk: raw => normalizeText(raw) === 'NAO',
      },
      {
        text: 'Percentual de vidas afastadas deve ser menor ou igual a 3%',
        resultIndex: 1,
        kind: 'percent',
        isOk: raw => parsePercentValue(raw) <= 3,
      },
      {
        text: 'Percentual de capital afastado deve ser menor ou igual a 3%',
        resultIndex: 2,
        kind: 'percent',
        isOk: raw => parsePercentValue(raw) <= 3,
      },
    ],
  },
  /*
   * Idade Máxima (`930016_UW_05`) — o critério muda com o tipo de seguro, que a
   * regra devolve no param 2. Params: `[quantidade, % quantidade, tipo de
   * seguro, % capital]`.
   *   - Seguro Novo: nenhuma vida acima de 70 anos.
   *   - Renovação/Transferência: uma linha — com vidas de 70 anos ou mais,
   *     quantidade e capital dessas vidas até 3% do grupo.
   * Mensagem antiga (sem o tipo) mantém as duas linhas de antes.
   */
  'ALBA-PA-UW-B9906': {
    label: 'Idade Máxima',
    order: 50,
    lines: [],
    resolveLines: params => {
      const insuranceType = params[2];
      if (insuranceType == null) return IDADE_MAXIMA_LEGACY_LINES;
      const normalized = normalizeText(insuranceType);
      const isNewInsurance =
        normalized.startsWith('SEGURO NOVO') ||
        normalized.startsWith('NAO INFORMADO');
      return isNewInsurance
        ? [IDADE_MAXIMA_NEW_INSURANCE_LINE]
        : [idadeMaximaRenewalLine(params)];
    },
  },
  'ALBA-PA-UW-B9907': {
    label: 'Idade Média',
    order: 60,
    lines: [
      {
        text: 'Idade média deve ser menor ou igual a 55 anos',
        resultIndex: 0,
        isOk: raw => parsePercentValue(raw) <= 55,
      },
    ],
  },
  /*
   * Natureza Jurídica — a regra `930016_UW_14` já classifica PRIVADA/PÚBLICA
   * pelo código IBGE do estipulante (1xx = Administração Pública reprova; todo
   * o resto libera) e manda só o veredito. Um param, uma linha.
   */
  'ALBA-PA-UW-B9908': {
    label: 'Natureza Jurídica',
    order: 65,
    lines: [
      {
        text: 'Deve ser igual a PRIVADA',
        resultIndex: 0,
        isOk: raw => normalizeText(raw) === 'PRIVADA',
      },
    ],
  },
  'ALBA-PA-UW-B9909': {
    label: 'Nível de Risco CNAE',
    order: 70,
    lines: [
      {
        text: 'Nível de risco CNAE deve ser igual a {0}',
        resultIndex: 1,
        isOk: (raw, params) =>
          (params[0] ?? 'BAIXO ou MEDIO')
            .split(/\s+ou\s+/i)
            .map(normalizeText)
            .includes(normalizeText(raw)),
      },
    ],
  },
  /*
   * Regra de Limite — limite ABSOLUTO de capital por cobertura (tabela do
   * produto). A regra `930016_UW_13_LimiteAbsolutoCobertura` devolve SEMPRE as
   * nove coberturas da tabela, na ordem abaixo, como pares
   * `[limite, capital contratado]` — 18 params. Cobertura não contratada volta
   * com capital R$ 0,00 e some da grade (`hideWhen`), então o subscritor vê só
   * as coberturas que o plano realmente vendeu.
   */
  'ALBA-PA-UW-B9913': {
    label: 'Regra de Limite',
    order: 90,
    lines: LIMITE_ABSOLUTO_COVERAGE_NAMES.map((coverageName, index) => ({
      text: `Limite Absoluto para a Cobertura "${coverageName}" atingindo`,
      resultIndex: index * 2 + 1,
      isOk: (raw: string, params: string[]) =>
        parseBrlValue(raw) <= parseBrlValue(params[index * 2] ?? ''),
      hideWhen: (raw: string) => !(parseBrlValue(raw) > 0),
    })),
  },
  /*
   * Limite de cobertura — capital contratado acima do limite de subscrição da
   * cobertura. Uma linha por cobertura reprovada, com o nome vindo do catálogo
   * do produto (código cru quando o catálogo ainda não carregou) e o capital
   * contratado na coluna Resultado.
   */
  'ALBA-PA-UW-B9914': {
    label: 'Limite de cobertura',
    order: 95,
    lines: [],
    resolveLines: (params, { detail, coverageNameByCode }) =>
      parseCoverageLimitItems(extractCoverageLimitSource(params, detail)).map(
        item => ({
          text: item.capital
            ? `${coverageNameByCode?.get(item.code) ?? item.code} (capital ${item.capital} / limite ${item.limit})`
            : item.code,
          resultIndex: null,
          rawResult: item.capital || undefined,
          isOk: (raw: string) =>
            parseBrlValue(raw) <= parseBrlValue(item.limit),
        })
      ),
  },
  /*
   * Limite de Capital — capital individual acima do limite permitido para a
   * quantidade de vidas do grupo. A mensagem chega pronta, com a regra e o
   * resultado no mesmo texto. Dois formatos:
   *  - legado, separado por `;`:
   *    `Limite do capital individual R$ 100.000,00 para o total de 1 vida
   *    ultrapassou o limite; Capital R$ 200.000,00, Grupo 1`;
   *  - atual, veredito no fim: `... para o total de 51 vidas em diante: SIM`.
   * A regra vai para a coluna de sub-regra e o veredito para a de resultado.
   * Sem veredito próprio: a linha herda o status do critério.
   */
  '930016_UW_17_IndividualCapitalLimitByLives': {
    label: 'Limite de Capital',
    order: 97,
    lines: [],
    resolveLines: (_params, { detail }) => {
      const parsed = splitIndividualCapitalLimitMessage(detail);
      if (!parsed) return [];
      return [
        {
          text: parsed.rule,
          resultIndex: null,
          rawResult: parsed.result,
        },
      ];
    },
  },
  /*
   * Teto da diária (`930016_UW_18_DailyBenefitCap`) e disponibilidade da
   * consulta Serasa (`930016_UW_27_SerasaDisponibilidade`). Fora do catálogo
   * a grade mostrava o código técnico. O texto já traz "nenhum" ou
   * "NAO_APLICAVEL"; a coluna Resultado fica só com o rótulo.
   */
  '930016_UW_18_DailyBenefitCap': {
    label: 'Teto da diária',
    order: 98,
    lines: [],
    resolveLines: (params, { detail }) =>
      sentinelCriterionLines(params, detail),
  },
  '930016_UW_27_SerasaDisponibilidade': {
    label: 'Consulta Serasa',
    order: 99,
    lines: [],
    resolveLines: (params, { detail }) =>
      sentinelCriterionLines(params, detail),
  },
  /*
   * Coberturas restritas (`930016_UW_28_CoverageRequiresSubscription`). A
   * mensagem lista as coberturas selecionadas que exigem subscrição. Os nomes
   * vão na mesma célula, um por linha; o critério tem um resultado só (SIM)
   * e um status só.
   */
  '930016_UW_28_CoverageRequiresSubscription': {
    label: 'Coberturas restritas',
    order: 99.5,
    lines: [],
    resolveLines: (params, { detail, coverageNameByCode }) => {
      const names = parseRestrictedCoverageNames(
        extractRestrictedCoveragesSource(params, detail),
        coverageNameByCode
      );
      if (names.length === 0) return [];
      return [
        {
          text: names.join('\n'),
          resultIndex: null,
          rawResult: 'SIM',
        },
      ];
    },
  },
  'ALBA-PA-UW-B9910': {
    label: 'Quantidade de vidas',
    order: 80,
    lines: [
      {
        text: 'Quantidade de vidas deve ser menor ou igual a {0}',
        resultIndex: 1,
        isOk: (raw, params) =>
          parsePercentValue(raw) <= parsePercentValue(params[0] ?? '500'),
      },
    ],
  },
  /*
   * ANÁLISE DE CRÉDITO (Serasa) — WI 2224. São oito critérios de número mais o
   * de disponibilidade da consulta. Cada mensagem traz quatro params no padrão
   * `[limiteQtd, qtd, limiteValor, valor]` e rende DUAS linhas independentes.
   *
   * Os agregados são materializados pelo portal nos campos `UwSerasa*` do
   * `PolicyCustomer` do estipulante — ver `group-life-serasa-credit.ts` na API.
   *
   * ATENÇÃO: os limites do PME (R$ 80.000, reprovando por quantidade OU valor)
   * NÃO são os do Empresarial (R$ 50.000, exigindo os dois). É intencional.
   */
  'ALBA-PA-UW-B9920': {
    label: 'Ações Judiciais',
    order: 100,
    lines: serasaCountAndAmountLines('Ações Judiciais'),
  },
  'ALBA-PA-UW-B9921': {
    label: 'Cheques',
    order: 105,
    lines: serasaCountAndAmountLines('Cheques'),
  },
  'ALBA-PA-UW-B9922': {
    label: 'Protestos',
    order: 110,
    lines: serasaCountAndAmountLines('Protestos'),
  },
  'ALBA-PA-UW-B9923': {
    label: 'PEFIN',
    order: 115,
    lines: serasaCountAndAmountLines('PEFIN'),
  },
  'ALBA-PA-UW-B9924': {
    label: 'REFIN',
    order: 120,
    lines: serasaCountAndAmountLines('REFIN'),
  },
  'ALBA-PA-UW-B9925': {
    label: 'Dívidas Vencidas',
    order: 125,
    lines: serasaCountAndAmountLines('Dívidas Vencidas'),
  },
  /*
   * Falência/Concordata/RJ não tem liberação automática: o limite é 0 tanto em
   * quantidade quanto em valor. Os limites seguem vindo nos params (0 e
   * R$ 0,00) para a grade não ter número hardcoded.
   */
  'ALBA-PA-UW-B9926': {
    label: 'Falência, Concordata e Recuperação Judicial',
    order: 130,
    lines: [
      {
        text: 'Quantidade de ocorrências deve ser igual a {0}',
        resultIndex: 1,
        isOk: raw => parsePercentValue(raw) === 0,
      },
      {
        text: 'Valor total deve ser igual a {2}',
        resultIndex: 3,
        isOk: raw => parseBrlValue(raw) === 0,
      },
    ],
  },
  'ALBA-PA-UW-B9927': {
    label: 'Score',
    order: 135,
    lines: [
      {
        text: 'Score de crédito deve ser maior ou igual a {0} pontos',
        resultIndex: 1,
        isOk: (raw, params) =>
          parsePercentValue(raw) >= parsePercentValue(params[0] ?? '401'),
      },
    ],
  },
  /*
   * Disponibilidade da consulta. Só aparece quando reprovou: consulta OK não é
   * critério a exibir, e prêmio abaixo do gatilho sai como U0. O resultado é o
   * `UwSerasaStatus` (INDISPONIVEL / SEM_REGISTRO / SEM_CNPJ), que diz ao
   * subscritor por que o crédito não pôde ser avaliado.
   */
  'ALBA-PA-UW-B9928': {
    label: 'Consulta de Crédito (Serasa)',
    order: 140,
    showOnlyWhenUnderAnalysis: true,
    lines: [
      {
        text: 'Consulta de crédito do estipulante deve estar disponível',
        resultIndex: 0,
        isOk: raw => normalizeText(raw) === 'OK',
      },
    ],
  },
};

const FALLBACK_ORDER = 999;

/**
 * Casa a mensagem com o catálogo pelo `messageCode` e, na falta dele, pelo
 * `ruleName` — regras novas podem ser catalogadas pelo nome técnico enquanto
 * o código da mensagem não estiver definido na plataforma.
 */
function findCatalogEntry(
  message: BusinessUnderwritingMessage
): CatalogEntry | undefined {
  const messageCode = message.messageCode?.trim();
  const ruleName = message.ruleName?.trim();
  return (
    (messageCode ? CATALOGO[messageCode] : undefined) ??
    (ruleName ? CATALOGO[ruleName] : undefined)
  );
}

/**
 * Separa o texto da regra `930016_UW_17` do veredito que vai na coluna
 * Resultado. O formato atual termina em `: SIM` / `: NÃO`; o legado usa `;`.
 */
function splitIndividualCapitalLimitMessage(
  detail: string
): { rule: string; result?: string } | null {
  const semicolon = detail.indexOf(';');
  if (semicolon >= 0) {
    const rule = detail.slice(0, semicolon).trim();
    if (!rule) return null;
    const result = detail.slice(semicolon + 1).trim();
    return { rule, result: result || undefined };
  }

  const verdict = detail.match(/:\s*(SIM|NÃO|NAO)\s*$/i);
  if (verdict?.index != null) {
    const rule = detail.slice(0, verdict.index).trim();
    if (!rule) return null;
    return { rule, result: verdict[1].trim() };
  }

  const rule = detail.trim();
  return rule ? { rule } : null;
}

function toCriterionStatus(uwLevelCode?: string): 'OK' | 'Análise' {
  const normalized = (uwLevelCode ?? '').trim().toUpperCase();
  return normalized === 'U1' || normalized === '1' ? 'Análise' : 'OK';
}

/** Interpola `{n}` com `params[n]`; placeholder sem param vira "—". */
function interpolate(text: string, params: string[]): string {
  return text.replace(
    /\{(\d+)\}/g,
    (_, index) => params[Number(index)] ?? DASH
  );
}

/** "11.11" | "11.11%" → "11,11%" (formato do protótipo). */
function formatPercent(raw: string): string {
  const value = raw.trim().replace(/%$/, '').replace('.', ',');
  return `${value}%`;
}

function formatResult(raw: string | undefined, kind?: 'percent'): string {
  if (raw == null || raw.trim() === '') return DASH;
  return kind === 'percent' ? formatPercent(raw) : raw.trim();
}

function parseMessageParams(raw: unknown): string[] {
  if (typeof raw !== 'string' || !raw.trim()) return [];
  try {
    const parsed: unknown = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed.map(value => String(value)) : [];
  } catch {
    return [];
  }
}

/** `MessageParams` chega nos `underwritingRuns` (não no `messages` mapeado);
 *  indexa por `UwRuleMessageId` para casar com cada mensagem. */
function extractMessageParamsById(
  underwritingRuns: Array<Record<string, unknown>>
): Map<number, string[]> {
  const paramsById = new Map<number, string[]>();
  for (const run of underwritingRuns) {
    const list = run.UnderwritingMessageList;
    if (!Array.isArray(list)) continue;
    for (const item of list) {
      if (!item || typeof item !== 'object') continue;
      const record = item as Record<string, unknown>;
      const id = record.UwRuleMessageId;
      if (typeof id !== 'number') continue;
      const params = parseMessageParams(record.MessageParams);
      if (params.length > 0) paramsById.set(id, params);
    }
  }
  return paramsById;
}

/** Sem params crus, extrai os `(resultado: X)` do texto renderizado, em ordem
 *  — um por sub-regra, pela convenção de autoria das mensagens. */
function extractResultsFromDetail(detail: string): string[] {
  return [...detail.matchAll(/resultado:\s*([^)]+)\)/gi)].map(m => m[1].trim());
}

/**
 * "nenhum" e "não aplicável" não são um número medido: a mensagem do InsureMO
 * já embute o valor (`: nenhum.` ou `(resultado: NAO_APLICAVEL)`) e o param
 * cru cai de novo na coluna Resultado. O texto fica só com a regra; a coluna
 * mostra o rótulo uma vez.
 */
function normalizeSentinel(raw: string): string {
  return raw
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toUpperCase()
    .replace(/[^A-Z]+/g, '');
}

function isRepeatedSentinel(raw: string): boolean {
  const key = normalizeSentinel(raw);
  return key === 'NENHUM' || key === 'NAOAPLICAVEL';
}

function formatRepeatedSentinel(raw: string): string {
  return normalizeSentinel(raw) === 'NENHUM' ? 'Nenhum' : 'Não aplicável';
}

const EMBEDDED_SENTINEL =
  /(?:nenhum|n[aã]o[\s_]*aplic[aá]vel)/i;

function sentinelCriterionLines(
  params: string[],
  detail: string
): CatalogLine[] {
  const text = params.some(isRepeatedSentinel)
    ? stripEmbeddedSentinel(detail)
    : detail.trim();
  if (!text) return [];
  const raw = params[0];
  return [
    {
      text,
      resultIndex: null,
      rawResult:
        raw == null
          ? undefined
          : isRepeatedSentinel(raw)
            ? formatRepeatedSentinel(raw)
            : raw,
    },
  ];
}

function stripEmbeddedSentinel(detail: string): string {
  const withoutParenthesis = detail.replace(
    new RegExp(`\\s*\\(\\s*resultado:\\s*${EMBEDDED_SENTINEL.source}\\s*\\)\\.?`, 'i'),
    ''
  );
  const withoutSuffix = withoutParenthesis.replace(
    new RegExp(`\\s*:\\s*${EMBEDDED_SENTINEL.source}\\s*\\.?\\s*$`, 'i'),
    ''
  );
  const trimmed = withoutSuffix.replace(/\s{2,}/g, ' ').trim();
  if (!trimmed) return detail.trim();
  return /[.!?]$/.test(trimmed) ? trimmed : `${trimmed}.`;
}

function toFallbackCriterion(
  message: BusinessUnderwritingMessage,
  params: string[]
): AcceptanceCriterionView {
  const detail = message.messageDetail?.trim() ?? '';
  const status = toCriterionStatus(message.uwLevelCode);
  const repeatsSentinel = params.some(isRepeatedSentinel);
  const rule =
    detail && repeatsSentinel ? stripEmbeddedSentinel(detail) : detail;
  return {
    name: message.ruleName?.trim() || message.messageCode?.trim() || DASH,
    rules: rule ? [rule] : [],
    results: params.map(param =>
      isRepeatedSentinel(param) ? formatRepeatedSentinel(param) : param
    ),
    // Sem catálogo não há veredito por linha: a única linha herda o status.
    ruleOk: rule ? [null] : [],
    lineStatus: rule ? [status] : [],
    status,
  };
}

function toCatalogCriterion(
  entry: CatalogEntry,
  message: BusinessUnderwritingMessage,
  params: string[],
  context: CriterionContext
): AcceptanceCriterionView {
  // Sem params crus (ex.: histórico antigo), os resultados vêm do texto
  // renderizado — 1 "(resultado: X)" por linha, na ordem das linhas.
  const extracted =
    params.length === 0
      ? extractResultsFromDetail(message.messageDetail ?? '')
      : null;

  const lines = entry.resolveLines
    ? entry.resolveLines(params, context)
    : entry.lines;

  // As linhas são casadas com o resultado ANTES do filtro `hideWhen`: o índice
  // do param é posicional na mensagem e não pode escorregar quando uma linha
  // some da grade.
  const visibleLines = lines
    .map((line, index) => ({
      line,
      raw:
        line.rawResult ??
        (line.resultIndex == null
          ? undefined
          : extracted
            ? extracted[index]
            : params[line.resultIndex]),
    }))
    .filter(({ line, raw }) => {
      if (!line.hideWhen) return true;
      try {
        return !line.hideWhen(raw ?? '', params);
      } catch {
        return true;
      }
    });

  const rawResults = visibleLines.map(({ raw }) => raw);

  const ruleOk = visibleLines.map(({ line, raw }) => {
    if (!line.isOk || raw == null || raw.trim() === '') return null;
    try {
      return line.isOk(raw, params);
    } catch {
      return null;
    }
  });

  const status = toCriterionStatus(message.uwLevelCode);

  /*
   * Status POR LINHA. Cada sub-regra responde por si: a que reprovou vai para
   * "Análise", a que passou fica "OK" — independente do veredito da outra e do
   * status do critério. Linha sem veredito próprio (`null`) herda o status do
   * critério, porque não há base para afirmar que passou.
   */
  const lineStatus = ruleOk.map(ok => {
    if (ok === true) return 'OK' as const;
    if (ok === false) return 'Análise' as const;
    return status;
  });

  return {
    name: entry.label,
    rules: visibleLines.map(({ line }) => interpolate(line.text, params)),
    results: visibleLines.map(({ line }, index) =>
      formatResult(rawResults[index], line.kind)
    ),
    ruleOk,
    lineStatus,
    status,
  };
}

/**
 * Monta a grade de critérios a partir da resposta de underwriting messages.
 * Critérios catalogados saem com nome amigável, sub-regras e resultados por
 * linha, ordenados pelo catálogo; mensagens desconhecidas caem no fallback
 * (nome técnico + texto integral) no fim da grade.
 */
export function mapUnderwritingMessagesToCriteria(
  data?: BusinessUnderwritingMessagesResponse,
  members?: AcceptanceCriterionMember[],
  options?: AcceptanceCriteriaOptions
): AcceptanceCriterionView[] {
  const paramsById = extractMessageParamsById(data?.underwritingRuns ?? []);

  return (data?.messages ?? [])
    .flatMap(message => {
      const messageCode = message.messageCode?.trim();
      if (messageCode && REGRAS_OCULTAS.has(messageCode)) return [];

      // Mensagem do InsureMO: params na execução. Mensagem gerada pelo portal
      // (sem execução): params já vêm na própria mensagem.
      const params =
        message.uwRuleMessageId != null
          ? (paramsById.get(message.uwRuleMessageId) ?? [])
          : (message.messageParams ?? []);
      const entry = findCatalogEntry(message);
      const effectiveParams = entry?.defaultParams
        ? entry.defaultParams.map((defaultValue, index) => {
            const value = params[index]?.trim();
            const shouldUseDefaultForZero =
              value != null &&
              parseBrlValue(value) === 0 &&
              entry.defaultWhenZeroParamIndexes?.includes(index);
            return !value || shouldUseDefaultForZero ? defaultValue : value;
          })
        : params;
      const criterionParams =
        message.messageCode?.trim() === 'ALBA-PA-UW-B9905' && members
          ? [
              ...effectiveParams.slice(0, 2),
              calculateAwayFromWorkCapitalPercentage(members),
              ...effectiveParams.slice(3),
            ]
          : effectiveParams;
      const criterion = entry
        ? toCatalogCriterion(entry, message, criterionParams, {
            detail: message.messageDetail?.trim() ?? '',
            coverageNameByCode: options?.coverageNameByCode,
          })
        : toFallbackCriterion(message, params);
      if (entry?.showOnlyWhenUnderAnalysis && criterion.status !== 'Análise') {
        return [];
      }
      // Critério catalogado que ficou sem NENHUMA sub-regra visível (todas
      // ocultadas por `hideWhen`, ou lista dinâmica vazia) não vira linha
      // vazia na grade.
      if (
        entry &&
        (entry.lines.length > 0 || entry.resolveLines) &&
        criterion.rules.length === 0
      ) {
        return [];
      }
      return [
        {
          order: entry?.order ?? FALLBACK_ORDER,
          criterion,
        },
      ];
    })
    .sort((a, b) => a.order - b.order)
    .map(item => item.criterion);
}
